<template>
  <div class="md:overflow-hidden h-screen">
    <div class="flex flex-col md:flex-row">
      <div class="p-2 flex flex-grow overflow-y-auto h-screen" v-if="!failedUser && !authenticated && !route.meta.open">
        <div class="w-full flex flex-col">
          <div class="m-2 p-2 bg-white rounded shadow flex flex-col">
            <Loading message="One moment please..." />
          </div>
        </div>
      </div>
      <LoginBoxV2 ref="loginBox" :appConfig="appConfig" :auth="auth" :request-password-reset="requestPasswordReset"
        v-if="failedUser && !authenticated && !route.meta.open"
        @reset="toast.success('If an account exists for that email address, a reset email will arrive shortly')"
        @error="toast.error($event.message)" @create-account="onCreateAccountTry"
        create-account-title="Create Flight Test Crew Account">
        <img class="mt-4 mb-2 mx-auto" :src="imageURL()" alt="" width="180" />
      </LoginBoxV2>
      <SideNav :show-nav="authenticated && !route.meta.open && !route.meta.helpChatWindow" :shownApps="state.shownAppsCiv" :altShownApps="state.shownAppsMil" :shownAppsLabel="'Civilian Apps'" :altShownAppsLabel="'Military Apps'"
        :shownRoutes="state.shownRoutes" @recents="$refs.dbRecents.toggle()" @search="$refs.dbSearch.toggle()"
        showButtons :showAppActions="appActions.length > 0">
        <template v-slot:app-actions v-if="appActions.length">
          <div class="p-2">
            <IconMenu :key="currentApp" :actions="appActions" :contextData="{ store, router, toast }"
              testid="app-actions-trigger" />
          </div>
        </template>
        <template v-slot:collapsed-buttons>
          <HelpChatAgentButton v-if="helpChatEnabled" compact @open="openHelpChat" />
        </template>
        <template v-slot:buttons>
          <IconButton icon="arrows-up-to-line" @click="store.state.sideNavOpen = false" />
          <button class="primary " v-tooltip="'ALT + R'" @click="$refs.dbRecents.toggle()">
            <font-awesome-icon class="mr-2" icon="sync-alt"></font-awesome-icon>
            Recents
          </button>
          <HelpChatAgentButton v-if="helpChatEnabled && store.state.sideNavOpen" @open="openHelpChat" />
        </template>
        <template v-slot:head>
          <div class="flex flex-row items-center mx-4">
            <div class="mt-4 mb-2 p-2 flex-grow cursor-pointer" @click="router.push('/')">

              <img class="" :src="imageURL()" alt="" width="180" />
            </div>
            <NotificationFab />
          </div>

        </template>
        <template v-slot:mid v-if="authenticated && !route.meta.open">

        </template>
        <template v-slot:foot>
          <AccountPopup v-if="bugForm" :form="bugForm" />
        </template>
      </SideNav>
      <div :class="route.meta.helpChatWindow
        ? 'p-0 overflow-hidden flex flex-grow min-w-0 h-screen'
        : [
          store.state.sideNavOpen ? 'pt-2' : 'pt-16',
          route.meta.fullBleed ? 'p-0 overflow-hidden' : 'p-2 md:overflow-y-auto',
          'flex flex-grow min-w-0 h-screen',
        ]" v-if="authenticated && !route.meta.open">
        <router-view></router-view>
      </div>
      <div class="md:p-8 flex flex-grow overflow-y-auto h-screen" v-if="route.meta.open === true">
        <router-view name="open-forms"></router-view>
      </div>
    </div>
    <DatabaseRecents ref="dbRecents" />
    <component v-if="activeAppAction" :is="activeAppAction.component" @close="activeAppAction = null" />
    <HelpChatModal ref="helpChatModal" v-if="helpChatEnabled && !route.meta.helpChatWindow" />
    <DynamicForm ref="bugForm" />
    <!-- Floating Action Button for Chat -->
    <!-- <button 
      v-if="authenticated && !route.meta.open"
      @click="$refs.dbSearch.toggle()"
      class="fixed bottom-6 right-6 w-14 h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 z-40"
      v-tooltip="'Help Chat (ALT + F)'"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    </button> -->
  </div>
</template>

<script setup>
import { computed, ref, reactive, shallowRef, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { LoginBoxV2, SideNav, IconButton, IconMenu, Loading } from "@comp/index.js"; // Use V2 for create-account support (FTC self-signup)
import { auth, callable, appConfig as rawAppConfig } from "./db.js";
import { initAuthState } from "./authState.js";
import { groupBy, sortBy, get } from "lodash";
import DatabaseRecents from "./views/DatabaseRecents.vue";
import HelpChatModal from "./views/Home/HelpChat/HelpChatModal.vue";
import HelpChatAgentButton from "./views/Home/HelpChat/HelpChatAgentButton.vue";
import { startHelpChatAlerts, stopHelpChatAlerts } from "./views/Home/HelpChat/helpChatAlerts.js";
import NotificationFab from "./components/NotificationFab.vue";
import AccountPopup from "./components/AccountPopup.vue";
import DynamicForm from "./components/DynamicForm/DynamicForm.vue";
import { useToast } from "vue-toastification";

const toast = useToast();
// use the store to determine the authenticated state
const store = useStore();
const helpChatEnabled = computed(() => store.getters.showBetaFeatures);

const router = useRouter();
const route = useRoute();

const requestPasswordReset = (email) => callable("sendPasswordResetEmail")({
  email,
  databaseURL: rawAppConfig.firebaseConfig.databaseURL,
});

const currentApp = computed(() => route.meta.app);
const failedUser = computed(() => store.state.failedUser);
const appActionModules = import.meta.glob("./views/*/appActions.js", { eager: true });
const activeAppAction = shallowRef(null);
const appActions = computed(() => {
  if (!authenticated.value || route.meta.open) return [];
  const actions = appActionModules[`./views/${currentApp.value}/appActions.js`]?.default || [];
  return actions.map(action => ({
    ...action,
    action: action.action || (() => { activeAppAction.value = action; }),
  }));
});

watch([currentApp, () => store.getters.authenticated, () => route.meta.open], () => {
  activeAppAction.value = null;
});

// Cache lock/warning results for side nav routes
const routeLockCache = ref(new Map());

// Load lock/warning results for side nav routes (async, outside computed)
const loadRouteLocks = async () => {
  if (!store.state.user) {
    routeLockCache.value.clear();
    return;
  }

  const newCache = new Map();
  const currentAppValue = currentApp.value;
  const relevantRoutes = router.getRoutes().filter(_route => {
    const isCurrentApp = _route.meta?.app === currentAppValue;
    const isSideNav = _route.meta?.sideNav;
    const isNotHidden = _route.meta.hidden && typeof _route.meta?.hidden === "function"
      ? !_route.meta.hidden(store.state.user)
      : true;
    return isCurrentApp && isSideNav && isNotHidden;
  });

  // Resolve all lock/warning functions in parallel
  await Promise.all(relevantRoutes.map(async (_route) => {
    let locked = [false, ""];
    let warning = [false, ""];

    if (_route.meta.lock && typeof _route.meta.lock === "function") {
      try {
        locked = await _route.meta.lock(store.state.user);
      } catch (e) {
        console.error("Failed to evaluate lock for route", _route.path, e);
      }
    }

    if (_route.meta.warning && typeof _route.meta.warning === "function") {
      try {
        warning = await _route.meta.warning(store.state.user);
      } catch (e) {
        console.error("Failed to evaluate warning for route", _route.path, e);
      }
    }

    newCache.set(_route.path, { locked, warning });
  }));

  routeLockCache.value = newCache;
};

// Reload locks when user or current app changes
watch([() => store.state.user, currentApp], () => {
  loadRouteLocks();
}, { immediate: true });

let state = reactive({
  shownApps: computed(() => {
    // Create a computed property that returns the filtered and sorted list of routes that are to be shown in the app switcher.
    const apps = router
      // Get all the routes.
      .getRoutes()
      // Flat the array of routes to a single level.
      .flat(Infinity)
      // Filter the routes based on whether they have a "appSwitcher" meta property.
      .filter((route) => {
        const show = typeof route.meta?.appSwitcher === "function" ? route.meta.appSwitcher(store.state.user) : route.meta?.appSwitcher;
        if (store.state.user?.CustomerID && !store.state.user?.Security) {
          return route.meta?.app === "CustomerPortal" && show;
        }
        return show;
      })
      // Filter the routes based on the security requirement.
      .filter((route) => {
        if (!route.meta?.security) return true;
        else
          return get(
            store?.state?.user?.Security,
            route?.meta?.security,
            false
          );
      })
      .filter((route) => {
        // Hide internal apps (Admin, Database, etc.) if user doesn't have internal class on this tenant
        const internalApps = ["Admin", "Database", "Military", "Audits", "Claims", "Leave", "Matrices"];
        // if (internalApps.includes(route.meta?.app)) {
        //   // Check internal class using store getter (synchronous)
        //   if (!store.getters.hasInternalClass) {
        //     return false;
        //   }
        // }

        // Claim-based filtering for Self routes
        const tenantClaim = store.state.tenantClaim;
        if (!!tenantClaim) {
          // FlightTestCrew Self: show when user has flight_test_crew claim
          if (route.path.includes("/FlightTestCrew/Self")) {
            return !!tenantClaim.flight_test_crew;
          }
          // Signatory Self (STC): show when user has signatory claim
          if (route.path.includes("/Matrices/SignatoryMatrix/Self")) {
            return !!tenantClaim.signatory;
          }
          // Military Signatory Self: show when user has signatory_military claim
          if (route.path.includes("/Matrices/MilitarySignatoryMatrix/Self")) {

            return !!tenantClaim.signatory_military;
          }
        }

        // Evaluate show() if it exists
        let passesShow = true;
        if (route.meta?.show && typeof route.meta?.show === "function") {

          passesShow = route.meta.show(store.state.user, { store });
        }

        // Evaluate hidden() if it exists
        let passesHidden = true;
        if (route.meta?.hidden && typeof route.meta?.hidden === "function") {
          passesHidden = !route.meta.hidden(store.state.user);
        }

        // Route is shown only if BOTH checks pass
        return passesShow && passesHidden;
      })
      // Sort the routes based on their app switcher position.
      .sort((a, b) => {
        const aPos = a.meta.appSwitcherPosition;
        const bPos = b.meta.appSwitcherPosition;
        return aPos > bPos ? 1 : bPos > aPos ? -1 : 0;
      });
    return apps;
  }),
  shownRoutes: computed(() => {
    // Create a computed property that returns the sorted and grouped list of routes that are to be shown in the side navigation.
    let sr = sortBy(
      // Sort the list of routes based on their side navigation position.
      router.getRoutes().filter((_route) => {
        const isCurrentApp = _route.meta?.app === currentApp.value;
        const isSideNav = _route.meta?.sideNav;
        const isNotHidden = _route.meta.hidden && typeof _route.meta?.hidden === "function" ? !_route.meta.hidden(store.state.user) : true;
        return isCurrentApp && isSideNav && isNotHidden;
      }),
      "meta.sideNavPosition"
    ).filter((route) => {
      if (route.meta?.show && typeof route.meta?.show === "function") {
        return route.meta.show(store.state.user);
      }
      return true;
    }).filter((route) => {
      // Hide internal matrix/admin sideNav items for pure externals
      // Pure externals: have FTC/signatory claims but no internal claim
      if (!store.getters.hasInternalClass) {
        // Hide matrix list apps and internal-only sections
        const internalOnlyApps = ["FlightTestCrew", "Matrices"];
        if (internalOnlyApps.includes(route.meta?.app)) {
          // Allow only Self routes, hide matrix lists
          if (!route.path.includes("/Self")) {
            return false;
          }
        }
      }
      return true;
    }).map((_route) => {
      // Use cached lock/warning results (synchronous)
      const cached = routeLockCache.value.get(_route.path) || { locked: [false, ""], warning: [false, ""] };

      return {
        ..._route,
        locked: cached.locked,
        warning: cached.warning,
      };
    });
    // Group the list of routes based on their side navigation section.
    return groupBy(sr, (i) => i.meta.sideNavSection || "catch all");
  }),
  shownAppsCiv: computed(() => {
    return state.shownApps.filter(app => {
      return app.meta.variant !== "military"
    })
  }),
  shownAppsMil: computed(() => {
    return state.shownApps.filter(app => {
      return app.meta.variant === "military";
    })
  }),
});

const authenticated = computed(() => store.getters.authenticated);
const appConfig = computed(() => store.state.appConfig);

// define the components in vue
const dbRecents = ref(null);
const loginBox = ref(null);
const bugForm = ref(null);

// get the correct image url
const imageURL = () => {
  const imgURL = new URL(
    `./assets/${appConfig.value.appPrefix}_logo_main.png`,
    import.meta.url
  );
  return imgURL;
};

// Wire up all authentication side effects (auth state listener, external
// redirect) and grab the handlers used in the template.
const { onCreateAccountTry, signout } = initAuthState({ store, router, toast, loginBox });

const helpChatModal = ref(null);
const documentHidden = ref(typeof document !== "undefined" && document.hidden);
const helpChatUserId = computed(() => {
  const id = store.getters.userID;
  return id && id !== "Unknown" ? id : null;
});
const helpChatVisible = computed(() => {
  if (!helpChatEnabled.value || documentHidden.value) return false;
  if (store.state.helpChatOpen) return true;
  return route.name === "Help Chat" || route.meta.helpChatWindow === true;
});

function syncDocumentHidden() {
  documentHidden.value = document.hidden;
}

function openHelpChat() {
  const target = store.state.helpChatUnreadTarget;
  if (target?.conversationId && target.messageId) {
    store.commit("save", {
      helpChatFocus: {
        conversationId: target.conversationId,
        messageId: target.messageId,
      },
    });
  }
  helpChatModal.value?.open();
}

onMounted(() => document.addEventListener("visibilitychange", syncDocumentHidden));
onUnmounted(() => {
  document.removeEventListener("visibilitychange", syncDocumentHidden);
  stopHelpChatAlerts();
});

watch([helpChatEnabled, helpChatUserId], ([enabled, userId]) => {
  stopHelpChatAlerts();
  if (!enabled || !userId) {
    store.commit("save", { helpChatUnread: 0, helpChatUnreadTarget: null });
    return;
  }
  startHelpChatAlerts({
    store,
    toast,
    userId,
    visible: helpChatVisible,
    openChat: () => helpChatModal.value?.open(),
  });
}, { immediate: true });

onkeydown = function (e) {
  if (helpChatEnabled.value && e.altKey && e.key == "f") {
    e.preventDefault();
    openHelpChat();
  }

  if (e.altKey && e.key == "r") {
    e.preventDefault();
    dbRecents.value.toggle();
  }
};
</script>
